import { documentIndexLiveWatch } from "@/lib/ui/document-index-live";
import {
  formatIndexEta,
  type DocumentIndexProgressView,
} from "@/lib/ui/document-progress";
import { showDocumentIndexBar } from "@/lib/ui/document-status";
import type { OpenRouterBalanceView } from "@/lib/ai/openrouter-balance-view";
import { formatSpendAmount } from "@/lib/ai/spend-format";
import type { HomeWidgetConfigMap } from "@/lib/ui/home-widget-config";
import {
  homeWidgetSpanFromConfig,
  homeWidgetUnitsFromConfig,
} from "@/lib/ui/home-widget-config";
import type { SessionRole } from "@/lib/auth/types";
import { canManageAi, canManageUsers } from "@/lib/auth/rbac";

export const HOME_WIDGETS_COOKIE = "pinwiki_home_widgets";
export const HOME_WIDGET_IDS = [
  "recent",
  "indexing",
  "quota",
  "spend",
  "spendusers",
  "server",
  "schedule",
] as const;
export const HOME_RECENT_LIMIT = 5;

export type HomeWidgetId = (typeof HOME_WIDGET_IDS)[number];

export const DEFAULT_HOME_WIDGETS: HomeWidgetId[] = [
  "recent",
  "indexing",
  "quota",
];

export const HOME_WIDGET_COLUMNS = 12;
export const HOME_WIDGET_UNIT = 4;
export const HOME_WIDGET_ADD_SPAN = 4;
export const HOME_CELLS_PER_ROW = HOME_WIDGET_COLUMNS / HOME_WIDGET_UNIT;
export const HOME_LAYOUT_GAP = "_";

export const HOME_WIDGET_SPAN: Record<HomeWidgetId, number> = {
  recent: 8,
  indexing: 8,
  quota: 4,
  spend: 4,
  spendusers: 8,
  server: 4,
  schedule: 4,
};

export const homeWidgetHint: Record<HomeWidgetId, string> = {
  recent: "Последние доступные файлы",
  indexing: "Что сейчас разбирается",
  quota: "Ваш расход и лимит AI",
  spend: "Баланс организации и расход AI",
  spendusers: "Топ-30 по тратам за месяц",
  server: "Нагрузка этой машины",
  schedule: "Вопросы, которые задаются сами",
};

export const homeWidgetCopy = {
  recent: "Недавние документы",
  indexing: "Индексация",
  quota: "Мой AI",
  spend: "Расход AI",
  spendusers: "Топ AI-расходов",
  server: "Сервер",
  schedule: "Автовопросы",
  remove: "Убрать",
  add: "Добавить",
  addWidget: "Добавить виджет",
  moveWidget: "Переместить",
  dropHere: "Сюда",
  emptyIndexing: "Сейчас ничего не индексируется",
  indexingEta: "Очередь",
  emptyRecentOwn: "Вы ещё не загружали документы",
  emptyRecentOwnHint: "Загрузите файл — он появится здесь.",
  emptySchedule: "Нет вопросов. Откройте настройки и добавьте вопрос.",
  scheduleWaiting: "Ждём следующего запуска",
  scheduleRunning: "Готовим ответ…",
  scheduleSettings: "Настройки",
  scheduleQuestion: "Вопрос",
  scheduleTime: "Время",
  scheduleCreate: "Добавить",
  scheduleSave: "Сохранить",
  scheduleLimit: "5 из 5",
  scheduleNext: "Следующий",
  scheduleLast: "Последний",
  scheduleOpenChat: "Чат",
  scheduleEnable: "Вкл",
  scheduleDisable: "Выкл",
  scheduleDelete: "Удалить",
  schedulePrevAnswer: "Предыдущий ответ",
  scheduleNextAnswer: "Следующий ответ",
  spendRemaining: "Остаток",
  spendUsage: "Израсходовано",
  spendExpenses: "Расходы",
  spendServiceChat: "Чат",
  spendServiceIndex: "Индексация",
  spendBalance: "Баланс",
  spendWalletIdle: "Счёт не открыт",
  spendUsd: "USD",
  spendRub: "RUB",
  spendBalanceNoKey: "Нет ключа",
  spendBalanceNoToken: "Нет токена",
  spendBalanceNoRates: "Нет тарифов",
  spendBalanceReady: "Тарифы есть",
  spendModel: "Модель",
  spendThinking: "Thinking",
  spendThinkingOn: "вкл",
  spendThinkingOff: "выкл",
  spendThinkWiki: "Wiki",
  spendThinkLlm: "LLM",
  spendDay: "Сутки",
  spendWeek: "Неделя",
  spendMonth: "Месяц",
  spendNoKey: "Нет ключа OpenRouter",
  spendNoKeyHint: "Укажите ключ в настройках AI.",
  spendNoTimewebKey: "Нет ключа Timeweb",
  spendNoTimewebKeyHint: "Укажите ключ в настройках AI.",
  spendNoTimewebAccountKey: "Нет токена аккаунта Timeweb",
  spendNoTimewebAccountKeyHint:
    "Без него расход в ₽ не считается. Укажите токен в настройках AI.",
  spendNoTimewebRates: "Нет тарифов Timeweb",
  spendNoTimewebRatesHint:
    "Не удалось получить цены Cloud API. Расход в ₽ сейчас не считается.",
  spendNoTimewebFinancesHint:
    "Не удалось получить баланс Cloud API. Проверьте право токена на финансы.",
  spendTimeweb: "Timeweb",
  spendOpenRouter: "OpenRouter",
  spendError: "Не удалось получить баланс",
  spendSettings: "Настройки AI",
  quotaRemaining: "Остаток за месяц",
  quotaSpent: "Израсходовано",
  quotaLimit: "Лимит за месяц",
  quotaCap: "лимит",
  quotaLimitUsd: "Лимит $",
  quotaLimitRub: "Лимит ₽",
  quotaUnlimited: "Без лимита",
  quotaMonth: "В этом месяце",
  emptySpendUsers: "В этом месяце ещё нет расходов",
  colUser: "Кто",
  quotaNone: "—",
  serverCpu: "CPU",
  serverMemory: "Память",
  serverDisk: "Диск",
  colDocument: "Документ",
  colType: "Тип",
  colUpdated: "Обновлён",
  colDepartment: "Отдел",
  colCollection: "Коллекция",
  colOwner: "Владелец",
  colProgress: "Прогресс",
  colVersion: "Версия",
  configWidth: "Ширина",
  configColumns: "Колонки",
  configBlocks: "Блоки",
  configCancel: "Отмена",
  configWidth1: "1 ячейка",
  configWidth2: "2 ячейки",
  configWidth3: "3 ячейки",
  configBlockBalance: "Баланс",
  configBlockSpent: "Расход",
  configBlockChart: "График",
  configBlockModel: "Модель",
  configBlockRemaining: "Остаток",
  configBlockLimit: "Лимит",
} as const;

export function homeWidgetOptionalColumnLabel(column: string): string {
  const labels: Record<string, string> = {
    type: homeWidgetCopy.colType,
    collection: homeWidgetCopy.colCollection,
    owner: homeWidgetCopy.colOwner,
    department: homeWidgetCopy.colDepartment,
    updated: homeWidgetCopy.colUpdated,
    version: homeWidgetCopy.colVersion,
    balance: homeWidgetCopy.configBlockBalance,
    spent: homeWidgetCopy.configBlockSpent,
    chart: homeWidgetCopy.configBlockChart,
    model: homeWidgetCopy.configBlockModel,
    remaining: homeWidgetCopy.configBlockRemaining,
    limit: homeWidgetCopy.configBlockLimit,
  };
  return labels[column] ?? column;
}

export function isHomeWidgetId(value: string): value is HomeWidgetId {
  return (HOME_WIDGET_IDS as readonly string[]).includes(value);
}

export function homeSpendWidgetTitle(
  provider: "OLLAMA" | "OPENROUTER" | "TIMEWEB",
): string {
  if (provider === "TIMEWEB") {
    return "Timeweb";
  }
  if (provider === "OPENROUTER") {
    return "OpenRouter";
  }
  return "Ollama";
}

export function spendWalletBalanceLabel(
  value: number | null | undefined,
  currency: "USD" | "RUB",
): string {
  if (value == null) {
    return homeWidgetCopy.spendWalletIdle;
  }
  return formatSpendAmount(value, currency);
}

export function spendServicesNote(
  services: { chat: number; index: number },
  currency: "USD" | "RUB",
): string {
  return `${homeWidgetCopy.spendServiceChat} ${formatSpendAmount(services.chat, currency)} · ${homeWidgetCopy.spendServiceIndex} ${formatSpendAmount(services.index, currency)}`;
}

export function spendOpenRouterBalanceLabel(
  balance: OpenRouterBalanceView,
): string {
  if (balance.status === "ok" && balance.remainingUsd != null) {
    return formatSpendAmount(balance.remainingUsd, "USD");
  }
  if (balance.status === "error") {
    return homeWidgetCopy.spendError;
  }
  return homeWidgetCopy.spendBalanceNoKey;
}

export function spendTimewebBalanceLabel(input: {
  hasTimewebKey: boolean;
  hasTimewebAccountKey: boolean;
  status: "ok" | "no_key" | "error";
  remainingRub: number | null;
}): string {
  if (!input.hasTimewebKey) {
    return homeWidgetCopy.spendBalanceNoKey;
  }
  if (!input.hasTimewebAccountKey) {
    return homeWidgetCopy.spendBalanceNoToken;
  }
  if (input.status === "ok" && input.remainingRub != null) {
    return formatSpendAmount(input.remainingRub, "RUB");
  }
  if (input.status === "error") {
    return homeWidgetCopy.spendError;
  }
  return homeWidgetCopy.spendBalanceNoToken;
}

export function homeCanUseWidget(
  id: HomeWidgetId,
  roles: SessionRole[],
): boolean {
  if (id === "spend") {
    return canManageAi(roles);
  }
  if (id === "spendusers") {
    return canManageAi(roles) || roles.includes("MANAGER");
  }
  if (id === "server") {
    return canManageUsers(roles);
  }
  return true;
}

export function homeAvailableWidgets(roles: SessionRole[]): HomeWidgetId[] {
  return HOME_WIDGET_IDS.filter((id) => homeCanUseWidget(id, roles));
}

export function defaultHomeWidgets(roles?: SessionRole[]): HomeWidgetId[] {
  if (roles && canManageUsers(roles)) {
    return [...DEFAULT_HOME_WIDGETS, "spend"];
  }
  return [...DEFAULT_HOME_WIDGETS];
}

export type HomeLayoutToken = HomeWidgetId | typeof HOME_LAYOUT_GAP;

export function homeWidgetUnits(
  id: HomeWidgetId,
  config: HomeWidgetConfigMap = {},
): number {
  return homeWidgetUnitsFromConfig(id, config);
}

export function homeWidgetSpan(
  id: HomeWidgetId,
  config: HomeWidgetConfigMap = {},
): number {
  return homeWidgetSpanFromConfig(id, config);
}

export function homeLayoutWidgetIds(layout: HomeLayoutToken[]): HomeWidgetId[] {
  const ids: HomeWidgetId[] = [];
  const seen = new Set<HomeWidgetId>();
  for (const token of layout) {
    if (token === HOME_LAYOUT_GAP || seen.has(token)) {
      continue;
    }
    seen.add(token);
    ids.push(token);
  }
  return ids;
}

function withAdminSpendDefault(
  layout: HomeLayoutToken[],
  roles?: SessionRole[],
): HomeLayoutToken[] {
  if (!roles || !canManageUsers(roles)) {
    return layout;
  }
  const ids = homeLayoutWidgetIds(layout);
  if (ids.includes("spend")) {
    return layout;
  }
  if (ids.join(",") !== DEFAULT_HOME_WIDGETS.join(",")) {
    return layout;
  }
  return [...layout, "spend"];
}

export function parseHomeLayout(
  raw: string | null | undefined,
  roles?: SessionRole[],
): HomeLayoutToken[] {
  const fallback = defaultHomeWidgets(roles);
  if (raw == null) {
    return [...fallback];
  }
  const value = raw.trim();
  if (value === "none") {
    return [];
  }
  if (value.length === 0) {
    return [...fallback];
  }
  const seen = new Set<HomeWidgetId>();
  const layout: HomeLayoutToken[] = [];
  for (const part of value.split(",")) {
    const token = part.trim();
    if (token === HOME_LAYOUT_GAP) {
      layout.push(HOME_LAYOUT_GAP);
      continue;
    }
    if (!isHomeWidgetId(token) || seen.has(token)) {
      continue;
    }
    seen.add(token);
    layout.push(token);
  }
  if (homeLayoutWidgetIds(layout).length === 0) {
    return [...fallback];
  }
  return withAdminSpendDefault(layout, roles);
}

export function parseHomeWidgets(
  raw: string | null | undefined,
  roles?: SessionRole[],
): HomeWidgetId[] {
  return homeLayoutWidgetIds(parseHomeLayout(raw, roles));
}

export function serializeHomeLayout(layout: HomeLayoutToken[]): string {
  const ids = homeLayoutWidgetIds(layout);
  if (ids.length === 0) {
    return "none";
  }
  const trimmed = [...layout];
  while (trimmed.at(-1) === HOME_LAYOUT_GAP) {
    trimmed.pop();
  }
  return trimmed.length === 0 ? "none" : trimmed.join(",");
}

export function serializeHomeWidgets(widgets: HomeWidgetId[]): string {
  return widgets.length === 0 ? "none" : widgets.join(",");
}

export function filterHomeLayout(
  layout: HomeLayoutToken[],
  canUse: (id: HomeWidgetId) => boolean,
): HomeLayoutToken[] {
  return layout.map((token) =>
    token === HOME_LAYOUT_GAP || canUse(token) ? token : HOME_LAYOUT_GAP,
  );
}

function homeLayoutToUnits(
  layout: HomeLayoutToken[],
  config: HomeWidgetConfigMap = {},
): (HomeWidgetId | null)[] {
  const units: (HomeWidgetId | null)[] = [];
  let column = 0;
  function padRow() {
    while (column > 0 && column < HOME_CELLS_PER_ROW) {
      units.push(null);
      column += 1;
    }
    if (column === HOME_CELLS_PER_ROW) {
      column = 0;
    }
  }
  for (const token of layout) {
    if (token === HOME_LAYOUT_GAP) {
      if (column + 1 > HOME_CELLS_PER_ROW) {
        padRow();
      }
      units.push(null);
      column += 1;
      if (column === HOME_CELLS_PER_ROW) {
        column = 0;
      }
      continue;
    }
    const size = homeWidgetUnits(token, config);
    if (column + size > HOME_CELLS_PER_ROW) {
      padRow();
    }
    for (let i = 0; i < size; i++) {
      units.push(token);
    }
    column += size;
    if (column === HOME_CELLS_PER_ROW) {
      column = 0;
    }
  }
  return units;
}

function homeUnitsToLayout(
  units: (HomeWidgetId | null)[],
  config: HomeWidgetConfigMap = {},
): HomeLayoutToken[] {
  const layout: HomeLayoutToken[] = [];
  for (let index = 0; index < units.length; ) {
    const cell = units[index];
    if (cell == null) {
      layout.push(HOME_LAYOUT_GAP);
      index += 1;
      continue;
    }
    layout.push(cell);
    index += homeWidgetUnits(cell, config);
  }
  return layout;
}

function alignHomeDest(index: number, size: number): number {
  const column = ((index % HOME_CELLS_PER_ROW) + HOME_CELLS_PER_ROW) %
    HOME_CELLS_PER_ROW;
  if (size > 1 && column + size > HOME_CELLS_PER_ROW) {
    return index - column + HOME_CELLS_PER_ROW;
  }
  return Math.max(0, index);
}

function findHomeFit(
  units: (HomeWidgetId | null)[],
  size: number,
  from: number,
): number | null {
  const start = Math.max(0, from);
  for (let index = start; index <= units.length; index++) {
    const column = index % HOME_CELLS_PER_ROW;
    if (column + size > HOME_CELLS_PER_ROW) {
      continue;
    }
    let fits = true;
    for (let offset = 0; offset < size; offset++) {
      if (units[index + offset] != null) {
        fits = false;
        break;
      }
    }
    if (fits) {
      return index;
    }
  }
  return null;
}

export function homeCellIndex(row: number, column: number): number {
  return (
    (Math.max(1, row) - 1) * HOME_CELLS_PER_ROW +
    Math.floor((Math.max(1, column) - 1) / HOME_WIDGET_UNIT)
  );
}

export function toggleHomeWidget(
  layout: HomeLayoutToken[],
  id: HomeWidgetId,
  config: HomeWidgetConfigMap = {},
): HomeLayoutToken[] {
  const units = homeLayoutToUnits(layout, config).map((cell) =>
    cell === id ? null : cell,
  );
  if (layout.includes(id)) {
    return homeUnitsToLayout(units, config);
  }
  const size = homeWidgetUnits(id, config);
  let start = findHomeFit(units, size, 0);
  if (start == null) {
    start = alignHomeDest(units.length, size);
    while (units.length < start + size) {
      units.push(null);
    }
  }
  for (let offset = 0; offset < size; offset++) {
    units[start + offset] = id;
  }
  return homeUnitsToLayout(units, config);
}

export function placeHomeWidget(
  layout: HomeLayoutToken[],
  id: HomeWidgetId,
  row: number,
  column: number,
  config: HomeWidgetConfigMap = {},
): HomeLayoutToken[] {
  const size = homeWidgetUnits(id, config);
  let units = homeLayoutToUnits(layout, config);
  const from = units.indexOf(id);
  const dest = alignHomeDest(homeCellIndex(row, column), size);
  units = units.map((cell) => (cell === id ? null : cell));
  while (units.length < dest + size) {
    units.push(null);
  }

  const displaced: HomeWidgetId[] = [];
  const seen = new Set<HomeWidgetId>();
  for (let index = dest; index < dest + size; index++) {
    const cell = units[index];
    if (cell && !seen.has(cell)) {
      seen.add(cell);
      displaced.push(cell);
    }
  }
  units = units.map((cell) =>
    cell && seen.has(cell) ? null : cell,
  );
  for (let offset = 0; offset < size; offset++) {
    units[dest + offset] = id;
  }

  let prefer = from >= 0 ? from : 0;
  for (const other of displaced) {
    const otherSize = homeWidgetUnits(other, config);
    let start = findHomeFit(units, otherSize, prefer);
    if (start == null) {
      start = findHomeFit(units, otherSize, 0);
    }
    if (start == null) {
      start = alignHomeDest(units.length, otherSize);
      while (units.length < start + otherSize) {
        units.push(null);
      }
    }
    for (let offset = 0; offset < otherSize; offset++) {
      units[start + offset] = other;
    }
    prefer = start + otherSize;
  }
  return homeUnitsToLayout(units, config);
}

export type HomeGridItemId = HomeWidgetId | "add";

export type HomeWidgetPlacement = {
  id: HomeGridItemId;
  row: number;
  column: number;
  span: number;
};

export type HomeWidgetSlot = {
  row: number;
  column: number;
  span: number;
};

export function homeWidgetPlacements(
  layout: readonly HomeLayoutToken[],
  showAdd: boolean,
  config: HomeWidgetConfigMap = {},
): HomeWidgetPlacement[] {
  const placements: HomeWidgetPlacement[] = [];
  let row = 1;
  let column = 1;
  function wrap(span: number) {
    if (column > 1 && column + span - 1 > HOME_WIDGET_COLUMNS) {
      row += 1;
      column = 1;
    }
  }
  function advance(span: number) {
    column += span;
    if (column > HOME_WIDGET_COLUMNS) {
      row += 1;
      column = 1;
    }
  }
  for (const token of layout) {
    if (token === HOME_LAYOUT_GAP) {
      wrap(HOME_WIDGET_UNIT);
      advance(HOME_WIDGET_UNIT);
      continue;
    }
    const span = homeWidgetSpan(token, config);
    wrap(span);
    placements.push({ id: token, row, column, span });
    advance(span);
  }
  if (showAdd) {
    wrap(HOME_WIDGET_ADD_SPAN);
    placements.push({
      id: "add",
      row,
      column,
      span: HOME_WIDGET_ADD_SPAN,
    });
  }
  return placements;
}

export function homeWidgetEmptyCells(
  occupied: readonly Pick<HomeWidgetPlacement, "row" | "column" | "span">[],
  rowCount: number,
  unit: number = HOME_WIDGET_ADD_SPAN,
): HomeWidgetSlot[] {
  const slots: HomeWidgetSlot[] = [];
  const rows = Math.max(rowCount, 1);
  for (let row = 1; row <= rows; row++) {
    for (let column = 1; column <= HOME_WIDGET_COLUMNS; column += unit) {
      const span = Math.min(unit, HOME_WIDGET_COLUMNS - column + 1);
      const taken = occupied.some(
        (item) =>
          item.row === row &&
          item.column < column + span &&
          column < item.column + item.span,
      );
      if (!taken) {
        slots.push({ row, column, span });
      }
    }
  }
  return slots;
}

export function hiddenHomeWidgets(
  widgets: HomeWidgetId[],
  available: readonly HomeWidgetId[] = HOME_WIDGET_IDS,
): HomeWidgetId[] {
  return available.filter((id) => !widgets.includes(id));
}

export function homeSeesAllDocuments(roles: SessionRole[]): boolean {
  return canManageUsers(roles);
}

export function homeRecentDocuments<T>(rows: T[]): T[] {
  return rows.slice(0, HOME_RECENT_LIMIT);
}

function indexingRowRank(
  row: Parameters<typeof documentIndexLiveWatch>[0],
): number {
  return showDocumentIndexBar(row.progress?.label) ? 0 : 1;
}

export function homeIndexingDocuments<
  T extends Parameters<typeof documentIndexLiveWatch>[0],
>(rows: T[]): T[] {
  return rows
    .filter(documentIndexLiveWatch)
    .sort((left, right) => indexingRowRank(left) - indexingRowRank(right));
}

export function homeIndexingQueueEtaMs(
  rows: Array<{ progress: DocumentIndexProgressView | null }>,
): number | null {
  const current = rows.find((row) => showDocumentIndexBar(row.progress?.label));
  const remaining = current?.progress?.remainingMs ?? null;
  if (remaining == null) {
    return null;
  }
  const queued = rows.filter((row) => row !== current).length;
  const full = current?.progress?.fullMs ?? remaining;
  return remaining + queued * full;
}

export function homeIndexingQueueEta(
  rows: Array<{ progress: DocumentIndexProgressView | null }>,
): string | null {
  const remaining = homeIndexingQueueEtaMs(rows);
  return remaining == null ? null : formatIndexEta(remaining);
}

export const HOME_SCHEDULE_ROTATE_MS = 5000;

export function homeScheduleAnswerSlides<
  T extends { lastAnswer: string | null },
>(rows: T[]): T[] {
  return rows.filter((row) => Boolean(row.lastAnswer?.trim()));
}

export function nextHomeScheduleSlide(
  index: number,
  count: number,
  step: 1 | -1,
): number {
  if (count <= 0) {
    return 0;
  }
  return (index + step + count) % count;
}
